import { Component } from '@theme/component';

const mapboxWindow = /** @type {Window & { mapboxgl?: any }} */ (window);
const mapboxgl = mapboxWindow.mapboxgl;

/**
 * @param {number} latitude
 * @param {number} elevationMeters
 */
function calculateGravityComponents(latitude, elevationMeters) {
  const latitudeValue = Number(latitude);
  const elevationValue = Number(elevationMeters);

  if (!Number.isFinite(latitudeValue) || !Number.isFinite(elevationValue)) {
    return null;
  }

  const rad = (latitudeValue * Math.PI) / 180;

  const g0 = 9.780327;
  const beta = 0.0053024;
  const gamma = 0.0000058;

  const seaLevelGravity =
    g0 *
    (1 +
      beta * Math.pow(Math.sin(rad), 2) -
      gamma * Math.pow(Math.sin(2 * rad), 2));
  const freeAirCorrection = 3.086e-6 * elevationValue;
  const localGravity = seaLevelGravity - freeAirCorrection;

  return {
    seaLevelGravity,
    freeAirCorrection,
    localGravity,
  };
}

const GRAVITAS_REFERENCE_GRAVITY = 9.801;
const DEFAULT_REFERENCE_GRAVITY = 9.801;
const KG_PER_LB = 0.45359237;
const KG_PER_OZ = KG_PER_LB / 16;
const GRAMS_PER_OUNCE = 28.349523125;

/**
 * @param {number} localGravity
 * @param {number} referenceGravity
 */
function calculateErrorPerKg(localGravity, referenceGravity) {
  return (localGravity / referenceGravity - 1) * 1000;
}

/**
 * @param {number} value
 */
function roundToScaleResolution(value) {
  return Math.round(value * 10) / 10;
}

/**
 * @param {number} value
 */
function roundToOunceResolution(value) {
  return Math.round(value * 1000) / 1000;
}

/**
 * @param {HTMLInputElement | null} input
 * @param {string} value
 */
function setInputValueIfIdle(input, value) {
  if (input && document.activeElement !== input) {
    input.value = value;
  }
}

/**
 * @param {number} kilograms
 */
function convertKgToLbOz(kilograms) {
  const safeKilograms = Math.max(0, kilograms);
  const totalPounds = safeKilograms / KG_PER_LB;
  let pounds = Math.floor(totalPounds);
  let ounces = (totalPounds - pounds) * 16;

  if (ounces >= 15.9995) {
    pounds += 1;
    ounces = 0;
  }

  return {
    pounds,
    ounces,
  };
}

/**
 * @param {number} pounds
 * @param {number} ounces
 */
function convertLbOzToKg(pounds, ounces) {
  const safePounds = Number.isFinite(pounds) ? Math.max(0, pounds) : 0;
  const safeOunces = Number.isFinite(ounces) ? Math.max(0, ounces) : 0;
  return safePounds * KG_PER_LB + safeOunces * KG_PER_OZ;
}

class TwLocalGravityCalc extends Component {
  /** @type {string | undefined} */
  mapboxToken;

  /** @type {any} */
  map = null;

  /** @type {any} */
  marker = null;

  /** @type {'m' | 'ft'} */
  distanceUnit = 'm';

  /** @type {'kg' | 'lb-oz'} */
  weightUnit = 'kg';

  /** @type {number} */
  latitude = 45.5;

  /** @type {number} */
  elevationMeters = 0;

  /** @type {number} */
  adjustmentWeightKg = 1;

  /** @type {number} */
  errorWeightKg = 1;

  /** @type {number} */
  referenceGravity = DEFAULT_REFERENCE_GRAVITY;

  /** @type {string} */
  location = 'Sea Level';

  /** @type {number | undefined} */
  lat;

  /** @type {number | undefined} */
  lon;

  connectedCallback() {
    super.connectedCallback();

    this.mapboxToken = this.dataset.mapboxToken;

    this.initEventListeners();
    this.updateResultsDisplay();
    this.initializeMap();
  }

  initEventListeners() {
    const locationForm = /** @type {HTMLFormElement | null} */ (
      this.querySelector('[data-location-form]')
    );
    if (locationForm) {
      locationForm.addEventListener('submit', this.handleLocationLookup);
    }

    const locationBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-location-lookup]')
    );
    if (locationBtn) {
      locationBtn.addEventListener('click', this.handleLocationLookup);
    }

    const geoBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-use-geolocation]')
    );
    if (geoBtn) {
      geoBtn.addEventListener('click', this.handleGeolocation);
    }

    const latitudeInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-input-latitude]')
    );
    if (latitudeInput) {
      latitudeInput.addEventListener('input', this.handleLatitudeChange);
    }

    const elevationInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-input-elevation]')
    );
    if (elevationInput) {
      elevationInput.addEventListener('input', this.handleElevationChange);
    }

    const distanceToggle = /** @type {HTMLSelectElement | null} */ (
      this.querySelector('[data-distance-toggle]')
    );
    if (distanceToggle) {
      distanceToggle.addEventListener('change', (event) => {
        const target = /** @type {HTMLSelectElement | null} */ (
          event.currentTarget
        );
        if (!target) {
          return;
        }

        this.distanceUnit = target.value === 'ft' ? 'ft' : 'm';
        this.updateResultsDisplay();
      });
    }

    const weightToggle = /** @type {HTMLSelectElement | null} */ (
      this.querySelector('[data-weight-toggle]')
    );
    if (weightToggle) {
      weightToggle.addEventListener('change', (event) => {
        const target = /** @type {HTMLSelectElement | null} */ (
          event.currentTarget
        );
        if (!target) {
          return;
        }

        this.weightUnit = target.value === 'lb-oz' ? 'lb-oz' : 'kg';
        this.updateResultsDisplay();
      });
    }

    const weightKgInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-adjustment-weight-kg]')
    );
    if (weightKgInput) {
      weightKgInput.addEventListener(
        'input',
        this.handleAdjustmentWeightKgChange,
      );
    }

    const weightLbInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-adjustment-weight-lb]')
    );
    if (weightLbInput) {
      weightLbInput.addEventListener(
        'input',
        this.handleAdjustmentWeightLbOzChange,
      );
    }

    const weightOzInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-adjustment-weight-oz]')
    );
    if (weightOzInput) {
      weightOzInput.addEventListener(
        'input',
        this.handleAdjustmentWeightLbOzChange,
      );
    }

    const referenceGravityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-reference-gravity-input]')
    );
    if (referenceGravityInput) {
      referenceGravityInput.addEventListener(
        'input',
        this.handleReferenceGravityChange,
      );
    }

    const errorWeightKgInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-error-weight-kg]')
    );
    if (errorWeightKgInput) {
      errorWeightKgInput.addEventListener(
        'input',
        this.handleErrorWeightKgChange,
      );
    }

    const errorWeightLbInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-error-weight-lb]')
    );
    if (errorWeightLbInput) {
      errorWeightLbInput.addEventListener(
        'input',
        this.handleErrorWeightLbOzChange,
      );
    }

    const errorWeightOzInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-error-weight-oz]')
    );
    if (errorWeightOzInput) {
      errorWeightOzInput.addEventListener(
        'input',
        this.handleErrorWeightLbOzChange,
      );
    }
  }

  handleLatitudeChange = () => {
    const latitudeInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-input-latitude]')
    );
    const value = parseFloat(String(latitudeInput?.value || '0'));

    if (Number.isNaN(value)) {
      return;
    }

    this.latitude = value;
    this.updateResultsDisplay();
  };

  handleElevationChange = () => {
    const elevationInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-input-elevation]')
    );
    const value = parseFloat(String(elevationInput?.value || '0'));

    if (Number.isNaN(value)) {
      return;
    }

    this.elevationMeters = this.distanceUnit === 'ft' ? value * 0.3048 : value;
    this.updateResultsDisplay();
  };

  handleAdjustmentWeightKgChange = () => {
    const weightKgInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-adjustment-weight-kg]')
    );
    const value = parseFloat(String(weightKgInput?.value || '0'));

    if (Number.isNaN(value)) {
      return;
    }

    this.adjustmentWeightKg = Math.max(0, value);
    this.updateResultsDisplay();
  };

  handleAdjustmentWeightLbOzChange = () => {
    const weightLbInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-adjustment-weight-lb]')
    );
    const weightOzInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-adjustment-weight-oz]')
    );

    const pounds = parseFloat(String(weightLbInput?.value || '0'));
    const ounces = parseFloat(String(weightOzInput?.value || '0'));

    this.adjustmentWeightKg = convertLbOzToKg(pounds, ounces);
    this.updateResultsDisplay();
  };

  handleReferenceGravityChange = () => {
    const referenceGravityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-reference-gravity-input]')
    );
    const value = parseFloat(String(referenceGravityInput?.value || '0'));

    if (Number.isNaN(value) || value <= 0) {
      return;
    }

    this.referenceGravity = value;
    this.updateResultsDisplay();
  };

  handleErrorWeightKgChange = () => {
    const errorWeightKgInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-error-weight-kg]')
    );
    const value = parseFloat(String(errorWeightKgInput?.value || '0'));

    if (Number.isNaN(value)) {
      return;
    }

    this.errorWeightKg = Math.max(0, value);
    this.updateResultsDisplay();
  };

  handleErrorWeightLbOzChange = () => {
    const errorWeightLbInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-error-weight-lb]')
    );
    const errorWeightOzInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-error-weight-oz]')
    );

    const pounds = parseFloat(String(errorWeightLbInput?.value || '0'));
    const ounces = parseFloat(String(errorWeightOzInput?.value || '0'));

    this.errorWeightKg = convertLbOzToKg(pounds, ounces);
    this.updateResultsDisplay();
  };

  /**
   * @param {Event} event
   */
  handleLocationLookup = async (event) => {
    event.preventDefault();

    const locationInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-location-input]')
    );
    const location = locationInput?.value?.trim();

    if (!location) {
      this.showError('Please enter a location');
      return;
    }

    this.showLoading(true);
    this.clearError();

    try {
      const geoData = await this.geocodeLocation(location);
      if (!geoData) {
        this.showError('Location not found');
        this.showLoading(false);
        return;
      }

      const { lat, lon, displayName } = geoData;
      const elevation = await this.fetchElevationFromGoogle(lat, lon);

      this.latitude = lat;
      this.elevationMeters = elevation;
      this.location = displayName;
      this.lat = lat;
      this.lon = lon;

      this.updateResultsDisplay();
      this.updateMap(lat, lon, displayName);

      this.showLoading(false);
    } catch (error) {
      console.error('Location lookup error:', error);
      this.showError('An error occurred during lookup');
      this.showLoading(false);
    }
  };

  /**
   * @param {Event} event
   */
  handleGeolocation = async (event) => {
    event.preventDefault();

    if (!navigator.geolocation) {
      this.showError('Geolocation is not supported by your browser');
      return;
    }

    this.showLoading(true);
    this.clearError();

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        try {
          const displayName = await this.reverseGeocode(lat, lon);
          const elevation = await this.fetchElevationFromGoogle(lat, lon);

          this.latitude = lat;
          this.elevationMeters = elevation;
          this.location = displayName || 'Current Location';
          this.lat = lat;
          this.lon = lon;

          this.updateResultsDisplay();
          this.updateMap(lat, lon, displayName || 'Current Location');

          this.showLoading(false);
        } catch (error) {
          console.error('Geolocation processing error:', error);
          this.showError('An error occurred processing your location');
          this.showLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        this.showError(
          'Unable to access your location. Please check permissions.',
        );
        this.showLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  /**
   * @param {number} weightKg
   */
  formatWeightLabel(weightKg) {
    if (this.weightUnit === 'lb-oz') {
      const { pounds, ounces } = convertKgToLbOz(weightKg);
      return `${pounds} lb ${ounces.toFixed(3)} oz`;
    }

    return `${weightKg.toFixed(3)} kg`;
  }

  /**
   * @param {number} errorPerKg
   */
  formatPerLoadError(errorPerKg) {
    const isLbOzUnit = this.weightUnit === 'lb-oz';
    const perLoadError = isLbOzUnit
      ? roundToOunceResolution((errorPerKg * KG_PER_LB) / GRAMS_PER_OUNCE)
      : roundToScaleResolution(errorPerKg);
    const loadLabel = isLbOzUnit ? '1 lb load' : '1 kg load';
    const unitLabel = isLbOzUnit ? 'oz' : 'g';
    const decimals = isLbOzUnit ? 3 : 1;

    return `${perLoadError.toFixed(decimals)} ${unitLabel} per ${loadLabel}`;
  }

  /**
   * @param {number} errorPerKg
   * @param {number} weightKg
   * @param {string} readingNoun
   */
  buildAdjustmentMessage(errorPerKg, weightKg, readingNoun) {
    const isLbOzUnit = this.weightUnit === 'lb-oz';
    const measurementError = isLbOzUnit
      ? roundToOunceResolution((errorPerKg * weightKg) / GRAMS_PER_OUNCE)
      : roundToScaleResolution(errorPerKg * weightKg);
    const unitLabel = isLbOzUnit ? 'oz' : 'g';
    const decimals = isLbOzUnit ? 3 : 1;
    const resolutionLabel = isLbOzUnit
      ? '0.001 oz scale resolution'
      : '0.1 g scale resolution';
    const weightLabel = this.formatWeightLabel(weightKg);
    const absValue = Math.abs(measurementError).toFixed(decimals);

    if (measurementError > 0) {
      return `At ${weightLabel}, subtract ${absValue} ${unitLabel} from the reading.`;
    }

    if (measurementError < 0) {
      return `At ${weightLabel}, add ${absValue} ${unitLabel} to the reading.`;
    }

    return `At ${weightLabel}, no adjustment is needed. Your ${readingNoun} is already within the ${resolutionLabel}.`;
  }

  /**
   * @param {number} weightKg
   * @param {{
   *   kgGroup: HTMLElement | null,
   *   lbOzGroup: HTMLElement | null,
   *   kgInput: HTMLInputElement | null,
   *   lbInput: HTMLInputElement | null,
   *   ozInput: HTMLInputElement | null,
   * }} refs
   */
  syncWeightInputs(weightKg, refs) {
    const { kgGroup, lbOzGroup, kgInput, lbInput, ozInput } = refs;

    if (kgGroup && lbOzGroup) {
      kgGroup.classList.toggle('hidden', this.weightUnit !== 'kg');
      lbOzGroup.classList.toggle('hidden', this.weightUnit !== 'lb-oz');
    }

    setInputValueIfIdle(kgInput, weightKg.toFixed(3));

    if (
      lbInput &&
      ozInput &&
      document.activeElement !== lbInput &&
      document.activeElement !== ozInput
    ) {
      const { pounds, ounces } = convertKgToLbOz(weightKg);
      lbInput.value = String(pounds);
      ozInput.value = ounces.toFixed(3);
    }
  }

  updateResultsDisplay() {
    const components = calculateGravityComponents(
      this.latitude,
      this.elevationMeters,
    );
    const resultGravity = /** @type {HTMLParagraphElement | null} */ (
      this.querySelector('[data-result-local-gravity]')
    );
    const resultPercent = /** @type {HTMLParagraphElement | null} */ (
      this.querySelector('[data-result-gravity-percent]')
    );
    const referenceGravityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-reference-gravity-input]')
    );
    const gravitasReferenceEl = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-gravitas-reference-gravity]')
    );
    const gravitasErrorEl = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-gravitas-error-grams]')
    );
    const gravitasAdjustmentMessageEl = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-gravitas-adjustment-message]')
    );
    const errorAdjustmentMessageEl = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-error-adjustment-message]')
    );
    const errorWeightKgInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-error-weight-kg]')
    );
    const errorWeightLbInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-error-weight-lb]')
    );
    const errorWeightOzInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-error-weight-oz]')
    );
    const errorWeightKgGroup = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-error-weight-kg-group]')
    );
    const errorWeightLbOzGroup = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-error-weight-lboz-group]')
    );
    const weightKgInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-adjustment-weight-kg]')
    );
    const weightLbInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-adjustment-weight-lb]')
    );
    const weightOzInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-adjustment-weight-oz]')
    );
    const weightKgGroup = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-weight-input-kg-group]')
    );
    const weightLbOzGroup = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-weight-input-lboz-group]')
    );
    const resultContainer = /** @type {HTMLDivElement | null} */ (
      this.querySelector('[data-result-gravity-container]')
    );

    if (!components || !resultGravity) {
      return;
    }
    const { localGravity } = components;
    const standardGravity = 9.80665;
    if (localGravity == null) {
      return;
    }

    resultGravity.textContent = `${localGravity.toFixed(5)} m/s²`;

    if (resultPercent) {
      const percent = (localGravity / standardGravity) * 100;
      resultPercent.textContent = `${percent.toFixed(4)}% of standard gravity`;
    }

    const normalizedReferenceGravity =
      this.referenceGravity > 0
        ? this.referenceGravity
        : DEFAULT_REFERENCE_GRAVITY;
    const errorPerKgReference = calculateErrorPerKg(
      localGravity,
      normalizedReferenceGravity,
    );
    const errorPerKgGravitas = calculateErrorPerKg(
      localGravity,
      GRAVITAS_REFERENCE_GRAVITY,
    );

    const normalizedWeightKg = Math.max(0, this.adjustmentWeightKg);
    this.adjustmentWeightKg = normalizedWeightKg;
    const normalizedErrorWeightKg = Math.max(0, this.errorWeightKg);
    this.errorWeightKg = normalizedErrorWeightKg;

    this.syncWeightInputs(normalizedErrorWeightKg, {
      kgGroup: errorWeightKgGroup,
      lbOzGroup: errorWeightLbOzGroup,
      kgInput: errorWeightKgInput,
      lbInput: errorWeightLbInput,
      ozInput: errorWeightOzInput,
    });

    this.syncWeightInputs(normalizedWeightKg, {
      kgGroup: weightKgGroup,
      lbOzGroup: weightLbOzGroup,
      kgInput: weightKgInput,
      lbInput: weightLbInput,
      ozInput: weightOzInput,
    });

    setInputValueIfIdle(
      referenceGravityInput,
      normalizedReferenceGravity.toFixed(5),
    );

    if (gravitasReferenceEl) {
      gravitasReferenceEl.textContent = `${GRAVITAS_REFERENCE_GRAVITY.toFixed(4)} m/s²`;
    }

    if (gravitasErrorEl) {
      gravitasErrorEl.textContent =
        this.formatPerLoadError(errorPerKgReference);
    }

    if (errorAdjustmentMessageEl) {
      errorAdjustmentMessageEl.textContent = this.buildAdjustmentMessage(
        errorPerKgReference,
        normalizedErrorWeightKg,
        'reading',
      );
    }

    if (gravitasAdjustmentMessageEl) {
      gravitasAdjustmentMessageEl.textContent = this.buildAdjustmentMessage(
        errorPerKgGravitas,
        normalizedWeightKg,
        'Gravitas reading',
      );
    }

    if (resultContainer) {
      resultContainer.classList.remove('gravity-result-animate');
      void resultContainer.offsetWidth;
      resultContainer.classList.add('gravity-result-animate');
    }

    const locationDisplay = /** @type {HTMLParagraphElement | null} */ (
      this.querySelector('[data-result-location]')
    );
    if (locationDisplay && this.location) {
      locationDisplay.textContent = this.location;
    }

    const latitudeInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-input-latitude]')
    );
    setInputValueIfIdle(latitudeInput, Number(this.latitude).toFixed(4));

    const elevationInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-input-elevation]')
    );
    const displayElevation =
      this.distanceUnit === 'ft'
        ? this.elevationMeters * 3.28084
        : this.elevationMeters;
    setInputValueIfIdle(
      elevationInput,
      displayElevation.toFixed(this.distanceUnit === 'ft' ? 0 : 1),
    );

    const distanceUnitLabel = this.querySelectorAll('.distance-unit');
    distanceUnitLabel.forEach((element) => {
      element.textContent = `(${this.distanceUnit})`;
    });

    if (this.map) {
      this.map.resize();
    }
  }

  /**
   * @param {string} location
   */
  async geocodeLocation(location) {
    if (!this.mapboxToken) {
      console.error('Mapbox token not configured');
      return null;
    }

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(location)}.json?access_token=${this.mapboxToken}&limit=1`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        return {
          lat: feature.center[1],
          lon: feature.center[0],
          displayName: feature.place_name,
        };
      }

      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }

  /**
   * @param {number} lat
   * @param {number} lon
   */
  async reverseGeocode(lat, lon) {
    if (!this.mapboxToken) {
      console.error('Mapbox token not configured');
      return null;
    }

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${this.mapboxToken}&limit=1`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        return data.features[0].place_name;
      }
      return null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  }

  /**
   * @param {number} latitude
   * @param {number} longitude
   */
  async fetchElevationFromGoogle(latitude, longitude) {
    try {
      const url = 'https://testapi-rl5tteg2ja-uc.a.run.app/elevation';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ latitude, longitude }),
      });
      const data = await response.json();
      if (data && data.elevation != null) {
        return Number(data.elevation);
      }
      return 0;
    } catch (error) {
      console.error('Google elevation error:', error);
      return 0;
    }
  }

  initializeMap() {
    const mapContainer = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-map-container]')
    );
    if (!mapContainer || !this.mapboxToken) return;

    if (mapboxgl) {
      const usCenter = { lat: 39.8283, lon: -98.5795 };
      mapboxgl.accessToken = this.mapboxToken;
      this.map = new mapboxgl.Map({
        container: mapContainer,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [usCenter.lon, usCenter.lat],
        zoom: 3.5,
      });

      this.map.addControl(new mapboxgl.NavigationControl());
      this.map.on('load', () => {
        this.map.resize();
      });

      this.map.on('click', async (/** @type {any} */ e) => {
        const clickedLat = e.lngLat.lat;
        const clickedLon = e.lngLat.lng;

        this.showLoading(true);
        this.clearError();

        try {
          const newDisplayName = await this.reverseGeocode(
            clickedLat,
            clickedLon,
          );
          const elevation = await this.fetchElevationFromGoogle(
            clickedLat,
            clickedLon,
          );

          this.latitude = clickedLat;
          this.elevationMeters = elevation;
          this.location =
            newDisplayName ||
            `${clickedLat.toFixed(4)}, ${clickedLon.toFixed(4)}`;
          this.lat = clickedLat;
          this.lon = clickedLon;

          this.updateResultsDisplay();

          if (this.marker) {
            this.marker.remove();
          }
          this.marker = new mapboxgl.Marker()
            .setLngLat([clickedLon, clickedLat])
            .setPopup(
              new mapboxgl.Popup().setHTML(`<strong>${this.location}</strong>`),
            )
            .addTo(this.map);

          this.marker.togglePopup();
          this.map.flyTo({ center: [clickedLon, clickedLat], zoom: 10 });

          this.showLoading(false);
        } catch (error) {
          console.error('Map click processing error:', error);
          this.showError('An error occurred processing the clicked location');
          this.showLoading(false);
        }
      });
    }
  }

  /**
   * @param {number} lat
   * @param {number} lon
   * @param {string} displayName
   */
  updateMap(lat, lon, displayName) {
    const mapContainer = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-map-container]')
    );
    if (!mapContainer || !this.mapboxToken) return;

    if (!this.map) {
      this.initializeMap();
    }

    if (this.map) {
      if (this.marker) {
        this.marker.remove();
      }

      this.marker = new mapboxgl.Marker()
        .setLngLat([lon, lat])
        .setPopup(
          new mapboxgl.Popup({ focusAfterOpen: false }).setHTML(
            `<strong>${displayName}</strong>`,
          ),
        )
        .addTo(this.map);
      this.marker.togglePopup();
      this.map.flyTo({ center: [lon, lat], zoom: 10 });
    }
  }

  /**
   * @param {boolean} isLoading
   */
  showLoading(isLoading) {
    const loader = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-loading]')
    );
    if (loader) {
      loader.classList.toggle('hidden', !isLoading);
    }

    const locationBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-location-lookup]')
    );
    if (locationBtn) {
      locationBtn.disabled = isLoading;
      locationBtn.style.display = isLoading ? 'none' : '';
    }

    const geoBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-use-geolocation]')
    );
    if (geoBtn) {
      geoBtn.disabled = isLoading;
      geoBtn.style.display = isLoading ? 'none' : '';
    }
  }

  /**
   * @param {string} message
   */
  showError(message) {
    const errorEl = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-error]')
    );
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    }
  }

  clearError() {
    const errorEl = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-error]')
    );
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    }
  }
}

if (!customElements.get('tw-local-gravity-calc')) {
  customElements.define('tw-local-gravity-calc', TwLocalGravityCalc);
}
